import { defineStore } from 'pinia'
import { NitroFetchOptions } from 'nitropack'
import { useSentry } from '~/plugins/sentry'
import { ensureBasketUid } from '~/utils/ensureBasketUid'
import GeneralResponse from '~/models/Api/GeneralResponse'
import PwaUserDataResponse from '~/models/Api/PwaUserDataResponse'
import GiftEventProductsResponse from '~/models/Api/GiftEventProductsResponse'
import Basket from '~/models/Basket/Basket'
import BasketProduct from '~/models/Basket/BasketProduct'
import BasketMarketPlaceSupplier from '~/models/Basket/BasketMarketPlaceSupplier'
import Product from '~/models/Product/Product'
import ProgressBar from '~/models/ProgressBar'
import HeadlineBar from '~/models/HeadlineBar'
import GiftEventProductsResponseGiftEvent from '~/models/Api/GiftEventProductsResponseGiftEvent'
import ProductAvailability from '~/models/Product/ProductAvailability'
import AuthUser from '~/models/AuthUser'
import BasketDiscount from '~/models/Basket/BasketDiscount'
import BasketVoucher from '~/models/Basket/BasketVoucher'
import BasketProductPrice from '~/models/Basket/BasketProductPrice'
import BasketRaiffeisenBankDiscountData from '~/models/Basket/BasketRaiffeisenBankDiscountData'
import BasketOrderStreakData from '~/models/Basket/BasketOrderStreakData'
import ProductDelivery from '~/models/Product/ProductDelivery'
import useUidCookie from '~/composables/useUidCookie'
import { pendingBasketChanges } from '~/utils/pendingBasketChanges'
import ViewBasketResponse from '~/models/Api/ViewBasketResponse'
import type { AddressFormData } from '~/types/addressForm'
import type ShippingListGroup from '~/models/Api/Cart/ShippingListGroup'
import type ShippingListShipping from '~/models/Api/Cart/ShippingListShipping'
import type PaymentListGroup from '~/models/Api/Cart/PaymentListGroup'
import type PaymentListPayment from '~/models/Api/Cart/PaymentListPayment'
import type PickupPlace from '~/models/Api/Cart/PickupPlace'

let customerSyncTimeout: ReturnType<typeof setTimeout> | null = null

export const useBasketStore = defineStore('basket', {
    state: () => ({
        productAmounts: {} as { [key: number]: number },
        productUnitPrices: {} as { [key: number]: BasketProductPrice },
        basket: {
            uid: '',
            updatedAt: 0,
            products: [],
            deposits: [],
            vouchers: [],
            invalidVouchers: [],
            totalAmount: 0,
            shipping: null,
            payment: null,
            newCustomer: null,
            discounts: [],
            price: {
                totalPrice: 0,
                afterDiscountPrice: 0,
                afterDiscountPriceNoVat: 0,
                productPrice: 0,
                absoluteDiscount: 0,
                absoluteProductsDiscount: 0,
                absoluteDiscountWithPremium: 0,
                absoluteDiscountWithPremiumWithDeliveryDiscount: 0,
                totalDepositPrice: 0,
            },
            freeDelivery: {
                isEnabled: false,
                isFree: false,
                fromPrice: 0,
                expressFromPrice: 0,
                standardFromPrice: 0,
                isFreeWithPremium: false,
            },
            discountedDelivery: {
                isEnabled: false,
                isDiscounted: false,
                fromPrice: 0,
            },
            credits: {
                credits: 0,
                creditsInMoney: 0,
                possibleCredits: 0,
            },
            hasActivePremiumMembership: false,
            containsCsrProducts: false,
            containsOtcProducts: false,
            containsProductNotInStock: false,
            showMarketingCheckbox: false,
            showSurveyCheckbox: false,
            premiumCareMembershipLogo: '',
            userPremiumInfo: null,
            rounding: 0,
            showExpressDeliveryModal: false,
            currencyRoundingAlert: '',
            unilabsCustomer: null,
            labTestDuplicities: [],
            marketPlaceData: [],
            raiffeisenBankDiscountData: {
                isEnabled: false,
                isCheckboxEnabled: false,
                isMarketingNotificationsCheckboxEnabled: false,
            } as BasketRaiffeisenBankDiscountData,
            hasPremiumFromGiftEvent: false,
            minOrderPrice: 0,
            isMinOrderPriceValidationMet: true,
            premiumProductSelected: null,
            ekoMessage: '',
            premiumAlertMessage: '',
            orderStreakData: {
                isEnabled: false,
                minimalPrice: 0,
                creditLevel: [],
                lastOrderStreak: null,
                appUrl: '',
                appQrImg: '',
            } as BasketOrderStreakData,
        } as Basket,
        deliveryDateText: '',
        uid: null as string | null,

        customAddonSaleCarousels: [] as { title: string; products: Product[] }[],
        customAddonSaleCarouselsAreLoading: false,
        addonSaleAreas: {} as { title: string; products: Product[] }[],
        addonSaleAreasAreLoading: false,

        // new

        authUser: null as AuthUser | null,
        favoriteCount: 0,
        favoriteProductIds: [] as number[],
        progressBars: [] as ProgressBar[],
        // null until user-data arrives - an anonymous visitor never fetches it, the layout config applies then
        headlineBars: null as HeadlineBar[] | null,
        gtmDataLayerUser: {
            type: 'anonymous',
        },
        voucherErrorMessage: '',
        giftEventActiveProducts: {} as { [key: number]: GiftEventProductsResponseGiftEvent },
        hasUserPremium: false,
        isInitialized: false,
        deliveries: [] as ProductDelivery[],
        fastestPossibleDeliveryDate: '',
        shippingGroups: [] as ShippingListGroup[],
        paymentGroups: [] as PaymentListGroup[],
        pickupPlaceForShipping: null as PickupPlace | null,
        addressSearchQuery: '',
    }),
    getters: {
        hasAddress(): boolean {
            const inv = (this as any).basket.newCustomer?.deliveryAddress

            return !!(inv?.street && inv?.city && inv?.postCode)
        },
        // a basket made entirely of Unilabs lab tests has nothing to ship - the delivery address is not collected in
        // step 1 for these, only in step 2 alongside the reservation details
        hasOnlyLabTestProducts(): boolean {
            const products = ((this as any).basket as Basket).products
            return products.length > 0 && products.every((product) => product.unilabsReservations.length > 0)
        },
        selectedShipping(): ShippingListShipping | null {
            const type = (this as any).basket.shipping?.type as string | undefined
            if (!type) return null
            for (const group of (this as any).shippingGroups as ShippingListGroup[]) {
                for (const shipping of group.shippings) {
                    if (shipping.friendlyShippingTypes.includes(type) && shipping.isActive) return shipping
                }
            }
            return null
        },
        selectedPayment(): PaymentListPayment | null {
            const type = (this as any).basket.payment?.type as string | undefined
            if (!type) return null
            for (const group of (this as any).paymentGroups as PaymentListGroup[]) {
                for (const payment of group.payments) {
                    if (payment.method === type && payment.isActive) return payment
                }
            }
            return null
        },
        marketPlaceProductsBySupplierId(): BasketMarketPlaceSupplier[] {
            return ((this as any).basket as Basket).marketPlaceData ?? []
        },
        // Products Pilulka ships itself, so they are what the Pilulka shipment is made of. Marketplace products are
        // shipped by their suppliers and lab tests are collected at a collection place, so neither belongs to it -
        // a basket without these products has no Pilulka shipment at all.
        pilulkaProducts(): BasketProduct[] {
            return ((this as any).basket as Basket).products.filter(
                (product) => !product.marketPlaceInfo && !product.unilabsReservations?.length
            )
        },
        // Marketplace products are stocked at their supplier (amountInStock 0), so they always trip the
        // backend `containsProductNotInStock` flag and would turn the Pilulka delivery-date text orange.
        // For the delivery-date warning we must ignore them: keep the backend flag unchanged for baskets
        // without marketplace products, otherwise warn only when a NON-marketplace product is not in stock.
        containsNonMarketPlaceProductNotInStock(): boolean {
            const basket = (this as any).basket as Basket
            if (!basket.containsProductNotInStock) return false
            if (!basket.products.some((product) => product.marketPlaceInfo)) return true
            return basket.products.some(
                (product) =>
                    !product.marketPlaceInfo && (product.status !== 1 || (product.amountInStock === 0 && !product.isOnTheWayFromSupplier))
            )
        },
    },
    actions: {
        async fetchData(makeInitialization: boolean = true, forceUid: string = ''): Promise<PwaUserDataResponse | null> {
            const uidCookie = useUidCookie()
            const uid = forceUid || uidCookie.value || null
            const postData = {
                uid,
            }
            const accessToken = useAccessTokenCookie().value || ''

            if (!uid && !accessToken) {
                this.isInitialized = true
                return null
            }

            const headers = {
                accessToken,
            }

            const requestData = {
                method: 'POST',
                headers,
                body: postData,
            }

            let response
            // if (isInitial && Date.now() / 1000 - parseInt(localStorage.getItem('userDataResponseLastUpdated')) < 60) {
            //     // init from localStorage
            //     response = JSON.parse(localStorage.getItem('userDataResponse'))
            // }
            if (!response) {
                try {
                    const userDataResponse: GeneralResponse<PwaUserDataResponse> = await $fetch(
                        `${useApiBaseUrl()}/api/v2/pwa/user-data`,
                        requestData as NitroFetchOptions<any>
                    )
                    response = userDataResponse.response

                    if (!response) {
                        useSentry()?.sentryCaptureException?.(
                            'User data response is undefined: ' +
                                `request data: ${JSON.stringify(requestData)};` +
                                `response: ${JSON.stringify(userDataResponse)}`
                        )

                        this.isInitialized = true
                        return null
                    }

                    // localStorage.setItem('userDataResponse', JSON.stringify(response))
                    // localStorage.setItem('userDataResponseLastUpdated', '' + Date.now() / 1000)
                } catch (e) {
                    // Cors for local development, hence try catch - should have not impact on production
                    this.isInitialized = true
                    return null
                }

                // localStorage.setItem('userDataResponse', JSON.stringify(response))
                // localStorage.setItem('userDataResponseLastUpdated', '' + Date.now() / 1000)
            }

            if (makeInitialization) {
                this.init(response)
            }

            this.isInitialized = true
            return response
        },

        init(data: PwaUserDataResponse) {
            this.setUid(data.uid || '')
            this.setAuthUser(data.authUser)
            this.setFavoriteCount(data.favoriteCount)
            this.setFavoriteProductIds(data.favoriteProductIds)
            this.setProgressBars(data.progressBars)
            this.setHeadlineBars(data.headlineBars || [])
            this.setGtmDataLayerUser(data.gtmDataLayerUser)
            this.setBasket(data.basket)
            this.setHasUserPremium(data.hasUserPremium)
        },

        setBasket(basket: Basket) {
            this.basket = basket
            const street = basket.newCustomer?.deliveryAddress?.street || ''
            const city = basket.newCustomer?.deliveryAddress?.city || ''
            this.addressSearchQuery = street && city ? `${street}, ${city}` : street

            const productsAndDeposits = [...basket.products, ...basket.deposits]
            productsAndDeposits.forEach((basketProduct) => {
                // a basket read that started before the customer's last change would put a just-deleted product back
                // until the debounced upsert lands - the local amount stays the truth until it's confirmed
                if (pendingBasketChanges.has(basketProduct.id)) {
                    return
                }
                if (basketProduct.id && basketProduct.amount) {
                    this.setProductAmount({
                        productId: basketProduct.id,
                        amount: basketProduct.amount,
                    })
                    this.setProductUnitPrice({
                        productId: basketProduct.id,
                        unitPrice: basketProduct.price,
                    })
                }
            })
        },

        async refreshDeliveryDateText() {
            const uidCookie = useUidCookie()
            const response = await useNuxtApp().$repository.viewBasketDeliveryDateText(uidCookie.value)
            if (!response?.response) return
            this.deliveryDateText = response.response.deliveryDate
        },

        async refreshDeliveries() {
            const uidCookie = useUidCookie()
            const response = await useNuxtApp().$repository.getFastestPossibleDeliveryDate(uidCookie.value)
            // No basket yet (empty uid) or an empty payload returns no `response`;
            // bail out instead of throwing during client init.
            if (!response?.response) return
            this.deliveries = response.response.deliveries
            this.fastestPossibleDeliveryDate = response.response.deliveryDate
        },

        // `basketUid` - pass it when the uid has just been generated: a freshly assigned uid cookie is written to
        // document.cookie only by Nuxt's watcher, so reading useUidCookie() again right away still gives the old value
        async updateCustomer(data: AddressFormData, immediately: boolean = false, basketUid?: string) {
            const existing = this.basket.newCustomer
            this.basket.newCustomer = {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phone: data.phone,
                phonePrefix: data.phonePrefix,
                note: data.note,
                hasCompanyInfo: data.hasCompanyInfo,
                deliveryAddress: {
                    street: data.deliveryAddress.street,
                    city: data.deliveryAddress.city,
                    postCode: data.deliveryAddress.postCode,
                    company: data.deliveryAddress.company,
                    district: existing?.deliveryAddress?.district ?? '',
                },
                invoiceAddress: {
                    company: data.invoiceAddress.company,
                    street: data.invoiceAddress.street,
                    city: data.invoiceAddress.city,
                    postCode: data.invoiceAddress.postCode,
                    district: existing?.invoiceAddress?.district ?? '',
                    ico: data.invoiceAddress.ico,
                    dic: data.invoiceAddress.dic,
                    icDph: data.invoiceAddress.icDph,
                },
                isSubscriber: existing?.isSubscriber ?? null,
                isSurveySubscriber: existing?.isSurveySubscriber ?? null,
            }
            if (customerSyncTimeout) clearTimeout(customerSyncTimeout)

            if (immediately) {
                const uid = basketUid ?? useUidCookie().value
                const accessToken = useAccessTokenCookie().value
                await useNuxtApp().$repository.createBasketUser({ uid, ...this.basket.newCustomer! }, accessToken)
            } else {
                customerSyncTimeout = setTimeout(() => {
                    const uid = useUidCookie().value
                    const accessToken = useAccessTokenCookie().value
                    useNuxtApp().$repository.createBasketUser({ uid, ...this.basket.newCustomer! }, accessToken)
                }, 1000)
            }
        },

        async updateCustomerSubscriptions(isSubscriber: boolean | null, isSurveySubscriber: boolean | null) {
            if (!this.basket.newCustomer) return
            this.basket.newCustomer.isSubscriber = isSubscriber
            this.basket.newCustomer.isSurveySubscriber = isSurveySubscriber
            const uid = useUidCookie().value
            const accessToken = useAccessTokenCookie().value
            await useNuxtApp().$repository.createBasketUser({ uid, ...this.basket.newCustomer }, accessToken)
        },

        async setAddress(street: string, city: string, postCode: string, keepDelivery: boolean = false) {
            this.addressSearchQuery = street && city ? `${street}, ${city}` : street
            const uid = await ensureBasketUid()

            if (!keepDelivery) {
                this.basket.shipping = null
                this.pickupPlaceForShipping = null
            }

            const existing = this.basket.newCustomer
            await this.updateCustomer(
                {
                    firstName: existing?.firstName ?? '',
                    lastName: existing?.lastName ?? '',
                    email: existing?.email ?? '',
                    phone: existing?.phone ?? '',
                    phonePrefix: existing?.phonePrefix ?? '',
                    note: existing?.note ?? '',
                    hasCompanyInfo: existing?.hasCompanyInfo ?? false,
                    deliveryAddress: {
                        street,
                        city,
                        postCode,
                        company: existing?.deliveryAddress?.company ?? '',
                    },
                    invoiceAddress: {
                        company: existing?.invoiceAddress?.company ?? '',
                        street: existing?.invoiceAddress?.street ?? '',
                        city: existing?.invoiceAddress?.city ?? '',
                        postCode: existing?.invoiceAddress?.postCode ?? '',
                        ico: existing?.invoiceAddress?.ico ?? '',
                        dic: existing?.invoiceAddress?.dic ?? '',
                        icDph: existing?.invoiceAddress?.icDph ?? '',
                    },
                },
                true,
                uid
            )
        },

        async loadOutOfStockAvailabilityTexts() {
            const config = useRuntimeConfig()
            const uidCookie = useUidCookie()
            const uid = uidCookie.value || null
            const requestData = {
                method: 'POST',
                body: { uid },
            }

            const response: GeneralResponse<{ availabilities: { availability: ProductAvailability; productId: number }[] }> = await $fetch(
                `${config.public.apiUrl}/api/v2/basket/availability-texts`,
                requestData as NitroFetchOptions<any>
            )
            if (response.code === 300 || !response.response?.availabilities) {
                return
            }

            const texts = response.response.availabilities
            texts.forEach((productTextPair) => {
                this.basket.products.forEach((product: BasketProduct) => {
                    if (product.id !== productTextPair.productId) {
                        return
                    }
                    product.availability = productTextPair.availability
                })
            })
        },

        async refreshBasketDataFromApi(successCallback: Function | null = null) {
            const uidCookie = useUidCookie()
            const response: GeneralResponse<ViewBasketResponse> = await useNuxtApp().$repository.getBasket(uidCookie.value)
            if (response === null || !response.result) {
                return
            }
            this.setBasket(response.response?.basket)
            this.progressBars = response.response?.progressBars || []
            if (successCallback !== null) {
                successCallback()
            }
        },

        setProgressBars(progressBars: ProgressBar[]) {
            this.progressBars = progressBars
        },

        setHeadlineBars(headlineBars: HeadlineBar[]) {
            this.headlineBars = headlineBars
        },

        setFavoriteProductIds(favoriteProductIds: number[]) {
            this.favoriteProductIds = favoriteProductIds
        },

        setFavoriteCount(favoriteCount: number) {
            this.favoriteCount = favoriteCount
        },

        setProductsPrice(productsPrice: number) {
            this.basket.price.productPrice = productsPrice
        },

        setAuthUser(authUser: AuthUser) {
            this.authUser = authUser
        },

        setUid(uid: string) {
            this.uid = uid
        },

        setTotalAmount(totalAmount: number) {
            this.basket.totalAmount = totalAmount
        },

        calculateFreeDelivery(afterDiscountPrice: number) {
            if (isNaN(afterDiscountPrice) || !this.basket.freeDelivery.fromPrice) {
                return
            }

            this.basket.price.afterDiscountPrice = afterDiscountPrice
            this.basket.freeDelivery.isFree = afterDiscountPrice >= this.basket.freeDelivery.fromPrice
            this.basket.discountedDelivery.isDiscounted = afterDiscountPrice >= this.basket.discountedDelivery.fromPrice
        },

        calculateAfterDiscountPrice() {
            let totalPrice = 0
            totalPrice += this.calculateProductsPrice()
            this.basket.discounts.forEach((discount: BasketDiscount) => {
                totalPrice += discount.price
            })
            this.basket.vouchers.forEach((voucher: BasketVoucher) => {
                totalPrice += voucher.price
            })

            return totalPrice
        },

        calculateProductsPrice() {
            let totalPrice = 0
            this.basket.products.forEach((product: BasketProduct) => {
                if (product.status === 1) {
                    totalPrice += product.price.price * (this.productAmounts[product.id] || 0)
                }
            })

            return totalPrice
        },

        calculateProductPriceAfterDiscount(productId: number) {
            let productPrice = 0
            this.basket.products.forEach((product: BasketProduct) => {
                if (productId === product.id && product.status === 1) {
                    productPrice += product.price.price * (this.productAmounts[product.id] || 0)
                }
            })

            this.basket.discounts.forEach((discount: BasketDiscount) => {
                if (productId === discount.productId) {
                    productPrice += discount.price
                }
            })

            return productPrice
        },

        calculateTotalAmount() {
            let totalAmount = 0
            this.basket.products.forEach((product: BasketProduct) => {
                totalAmount += this.productAmounts[product.id] || 0
            })

            return totalAmount
        },

        changeAmount(productId: number, amount: number) {
            this.productAmounts[productId] = amount
        },

        setProductAmount({ productId, amount }: { productId: number; amount: number }) {
            this.productAmounts[productId] = amount
        },

        setProductUnitPrice({ productId, unitPrice }: { productId: number; unitPrice: BasketProductPrice }) {
            setTimeout(() => {
                this.productUnitPrices[productId] = unitPrice
            }, 2000)
        },

        setCustomAddonSaleCarousels(carousels: { title: string; products: Product[] }[]) {
            this.customAddonSaleCarousels = carousels
        },

        setCustomAddonSaleCarouselsAreLoading(customAddonSaleCarouselsAreLoading: boolean) {
            this.customAddonSaleCarouselsAreLoading = customAddonSaleCarouselsAreLoading
        },

        setAddonSaleAreas(areas: { title: string; products: Product[] }[]) {
            this.addonSaleAreas = areas
        },

        setAddonSaleAreasAreLoading(addonSaleAreasAreLoading: boolean) {
            this.addonSaleAreasAreLoading = addonSaleAreasAreLoading
        },

        setGtmDataLayerUser(gtmDataLayerUser: any) {
            this.gtmDataLayerUser = gtmDataLayerUser
        },

        addBasketProduct(product: BasketProduct) {
            this.basket.products.push(product)
            this.basket.products = [...new Map(this.basket.products.map((item: BasketProduct) => [item.id, item])).values()]
        },

        setVoucherErrorMessage(message: string) {
            this.voucherErrorMessage = message
        },

        setActiveProducts(giftEventProductsResponse: GiftEventProductsResponse) {
            giftEventProductsResponse.giftEvents.forEach((giftEventProducts) => {
                this.giftEventActiveProducts[giftEventProducts.id] = giftEventProducts
            })
        },

        getGiftEventActiveProducts(giftEventId: number): GiftEventProductsResponseGiftEvent {
            return this.giftEventActiveProducts[giftEventId] || []
        },

        setHasUserPremium(hasUserPremium: boolean) {
            this.hasUserPremium = hasUserPremium
        },

        setShippingGroups(shippingGroups: ShippingListGroup[]) {
            this.shippingGroups = shippingGroups
        },

        setPaymentGroups(paymentGroups: PaymentListGroup[]) {
            this.paymentGroups = paymentGroups
        },

        hasProduct(productId: number) {
            return this.productAmounts[productId] > 0
        },

        getProductIds(): number[] {
            return Object.keys(this.productAmounts).map(Number)
        },
    },
})
